// frontend/src/components/import-export/SupplierResourceImportExport.tsx
// Bulk import / export of Supplier ↔ Resource catalog links.
//
// Reuses the generic ImportExportDialog for review, conflict resolution,
// duplicate grouping, progress, and result display.

import React, { useState } from "react";
import { Truck, Package, DollarSign, Star, type LucideIcon } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ImportExportDialog,
  type ImportExportConfig,
  type ImportValidationError,
} from "./ImportExportDialog";
import {
  fetchSupplierResourceLinks,
  importSupplierResourceLinks,
  type SupplierResourceLink,
} from "../../services/supplierResourceService";

// =============================================================================
// Helpers
// =============================================================================

function formatPrice(v: any): string {
  const n = Number(v);
  if (!isFinite(n) || n === 0) return "—";
  return `${n.toFixed(2)} $`;
}

function formatInt(v: any): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toString();
}

function formatBool(v: any): string {
  return v === true ? "Oui" : "Non";
}

/** One-line summary for the compact view inside duplicate compare cards. */
function renderLinkSummary(item: SupplierResourceLink): React.ReactNode {
  const cost = item.link.unitCost ? `${item.link.unitCost.toFixed(2)} $` : "Coût non défini";
  return (
    <span className="text-[var(--muted-foreground)]">
      <span className="text-[var(--foreground)]">{item.supplier.name}</span>
      <span className="opacity-75"> → {item.resource.name}</span>
      <span className="opacity-75"> · {cost}</span>
    </span>
  );
}

/**
 * Mid-density view for the compare panel: chips for each link metric so the
 * user can scan many cards quickly. Matches the ingredient-icon pattern from
 * RecipeImportExport.
 */
function renderLinkMedium(item: SupplierResourceLink): React.ReactNode {
  const chips: { icon: LucideIcon; color: string; label: string }[] = [];
  if (item.link.unitCost > 0) {
    chips.push({
      icon: DollarSign,
      color: "text-[var(--color-olive)]",
      label: `${item.link.unitCost.toFixed(2)} $`,
    });
  }
  if (item.link.minOrderQty != null && item.link.minOrderQty > 0) {
    chips.push({
      icon: Package,
      color: "text-[var(--color-steel)]",
      label: `MOQ ${item.link.minOrderQty}`,
    });
  }
  if (item.link.leadTimeDays != null && item.link.leadTimeDays > 0) {
    chips.push({
      icon: Truck,
      color: "text-[var(--color-amber)]",
      label: `${item.link.leadTimeDays} j`,
    });
  }
  if (item.link.supplierSku) {
    chips.push({
      icon: Package,
      color: "text-[var(--muted-foreground)]",
      label: `SKU ${item.link.supplierSku}`,
    });
  }
  if (item.link.isPreferred) {
    chips.push({
      icon: Star,
      color: "text-[var(--color-amber)]",
      label: "Préféré",
    });
  }
  if (chips.length === 0) {
    return (
      <span className="text-[var(--muted-foreground)] italic text-xs">
        Aucune information sur ce lien
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
      {chips.map((c, i) => {
        const Icon = c.icon;
        return (
          <span key={i} className="inline-flex items-center gap-1">
            <Icon className={`w-3.5 h-3.5 ${c.color}`} strokeWidth={2.25} />
            <span className="text-[var(--foreground)]">{c.label}</span>
          </span>
        );
      })}
    </div>
  );
}

/** Full detail view shown when the user expands the chevron in a compare card. */
function renderLinkDetail(item: SupplierResourceLink): React.ReactNode {
  const rows: { label: string; value: string }[] = [
    { label: "Fournisseur", value: item.supplier.name },
    { label: "Code fournisseur", value: item.supplier.code || "—" },
    { label: "Ressource", value: item.resource.name },
    { label: "Veloce ID ressource", value: item.resource.veloceResourceId || "—" },
    { label: "SKU fournisseur", value: item.link.supplierSku || "—" },
    { label: "Coût unitaire", value: formatPrice(item.link.unitCost) },
    { label: "Quantité min.", value: formatInt(item.link.minOrderQty) },
    { label: "Délai (jours)", value: formatInt(item.link.leadTimeDays) },
    { label: "Préféré", value: formatBool(item.link.isPreferred) },
  ];
  return (
    <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs">
      {rows.map((r, i) => (
        <React.Fragment key={i}>
          <span className="text-[var(--muted-foreground)]">{r.label}</span>
          <span className="text-[var(--foreground)] tabular-nums">{r.value}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// =============================================================================
// Config
// =============================================================================

const linkConfig: ImportExportConfig<SupplierResourceLink> = {
  entityName: "lien fournisseur-ressource",
  entityNamePlural: "liens fournisseur-ressource",

  // Identity: composite (supplier, resource), each side preferring its stable
  // identifier (code / veloceResourceId) when present.
  getKey: (item) => {
    const supKey = item.supplier.code
      ? `code:${item.supplier.code}`
      : `name:${item.supplier.name.toLowerCase()}`;
    const resKey = item.resource.veloceResourceId
      ? `vid:${item.resource.veloceResourceId}`
      : `name:${item.resource.name.toLowerCase()}`;
    return `${supKey}|${resKey}`;
  },

  getDisplayName: (item) => `${item.supplier.name} → ${item.resource.name}`,

  // Group rows by supplier in the category filter dropdown.
  getCategory: (item) => item.supplier.name,

  comparisonFields: [
    { label: "Fournisseur", key: "supplier.name", format: (v) => v || "—" },
    { label: "Ressource", key: "resource.name", format: (v) => v || "—" },
    { label: "SKU fournisseur", key: "link.supplierSku", format: (v) => v || "—" },
    { label: "Coût unitaire", key: "link.unitCost", format: formatPrice },
    { label: "Quantité min.", key: "link.minOrderQty", format: formatInt },
    { label: "Délai (jours)", key: "link.leadTimeDays", format: formatInt },
    { label: "Préféré", key: "link.isPreferred", format: formatBool },
  ],

  isIdentical: (a, b) => {
    const al = a.link;
    const bl = b.link;
    return (
      Math.abs((al.unitCost ?? 0) - (bl.unitCost ?? 0)) < 0.001 &&
      (al.minOrderQty ?? null) === (bl.minOrderQty ?? null) &&
      (al.leadTimeDays ?? null) === (bl.leadTimeDays ?? null) &&
      (al.supplierSku ?? null) === (bl.supplierSku ?? null) &&
      al.isPreferred === bl.isPreferred
    );
  },

  // If the existing link has no cost set, treat the incoming cost as a simple
  // update — auto-selected, no conflict resolution required.
  isUpdate: (incoming, existing) =>
    (existing.link.unitCost == null || existing.link.unitCost === 0) &&
    incoming.link.unitCost > 0,

  renderSummary: renderLinkSummary,
  renderMedium: renderLinkMedium,
  renderDetail: renderLinkDetail,

  getItemDate: (item) => item._createdAt,
};

// =============================================================================
// Validation
// =============================================================================

function validateLinkImport(parsed: any): ImportValidationError[] {
  const errors: ImportValidationError[] = [];
  if (typeof parsed !== "object" || parsed === null) {
    errors.push({ severity: "error", message: "Le fichier ne contient pas un objet JSON valide." });
    return errors;
  }
  const items = Array.isArray(parsed) ? parsed : parsed.data;
  if (!items || !Array.isArray(items)) {
    errors.push({
      severity: "error",
      message: "Structure incompatible : le fichier doit contenir un tableau « data » avec les liens.",
    });
    return errors;
  }
  if (items.length === 0) {
    errors.push({ severity: "error", message: "Le fichier ne contient aucun lien." });
    return errors;
  }

  let missingSupplier = 0;
  let missingResource = 0;
  for (const it of items) {
    if (!it?.supplier?.name) missingSupplier++;
    if (!it?.resource?.name) missingResource++;
  }
  if (missingSupplier > 0) {
    errors.push({
      severity: "error",
      message: `${missingSupplier} lien(s) sans nom de fournisseur. Chaque entrée doit avoir « supplier.name ».`,
    });
  }
  if (missingResource > 0) {
    errors.push({
      severity: "error",
      message: `${missingResource} lien(s) sans nom de ressource. Chaque entrée doit avoir « resource.name ».`,
    });
  }
  return errors;
}

function normalizeLinkData(parsed: any): SupplierResourceLink[] {
  const items: any[] = Array.isArray(parsed) ? parsed : parsed.data || [];
  return items.map((it) => ({
    _createdAt: it._createdAt,
    supplier: {
      name: it.supplier?.name ?? "",
      code: it.supplier?.code ?? null,
    },
    resource: {
      name: it.resource?.name ?? "",
      veloceResourceId: it.resource?.veloceResourceId ?? null,
    },
    link: {
      supplierSku: it.link?.supplierSku ?? null,
      unitCost: typeof it.link?.unitCost === "number" ? it.link.unitCost : 0,
      minOrderQty: typeof it.link?.minOrderQty === "number" ? it.link.minOrderQty : null,
      leadTimeDays: typeof it.link?.leadTimeDays === "number" ? it.link.leadTimeDays : null,
      isPreferred: it.link?.isPreferred === true,
    },
  }));
}

// =============================================================================
// Component
// =============================================================================

interface SupplierResourceImportExportProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SupplierResourceImportExport({
  isOpen,
  onClose,
}: SupplierResourceImportExportProps) {
  const queryClient = useQueryClient();
  // Holds the user's preference for auto-creating missing suppliers; default ON
  // per the design decision. Currently no UI surface to flip it (could be added
  // to the upload screen later); the value flows straight into the service.
  const [autoCreateSuppliers] = useState(true);

  const { data: existingItems = [] } = useQuery({
    queryKey: ["supplier-resource-links"],
    queryFn: fetchSupplierResourceLinks,
    enabled: isOpen,
  });

  const handleExport = (items: SupplierResourceLink[]) => {
    const exportData = {
      exportedAt: new Date().toISOString(),
      version: "1.0",
      description: "Supplier-resource catalog links",
      count: items.length,
      data: items,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `supplier_resources_${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (
    items: { data: SupplierResourceLink; resolution?: "incoming" | "existing" }[],
    onProgress?: (current: number, total: number) => void,
  ) => {
    const result = await importSupplierResourceLinks(items, {
      autoCreateSuppliers,
      onProgress,
    });
    queryClient.invalidateQueries({ queryKey: ["supplier-resource-links"] });
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
    queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    return result;
  };

  return (
    <ImportExportDialog<SupplierResourceLink>
      isOpen={isOpen}
      onClose={onClose}
      config={linkConfig}
      existingItems={existingItems}
      onExport={handleExport}
      onImport={handleImport}
      parseImportData={normalizeLinkData}
      validateImportData={validateLinkImport}
    />
  );
}
